import { TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';
import { joinFieldPath } from '../utils';

// Naive, but should work in most cases
const areObjectsEqual = (previousValue: unknown, newValue: unknown): boolean =>
    JSON.stringify(previousValue) === JSON.stringify(newValue);

const resolveField = (object: unknown, field: string): unknown =>
    typeof object === 'object' && object != null && field in object
        ? (object as Record<typeof field, unknown>)[field]
        : undefined;

const resolveFieldArray = (object: unknown, field: string): unknown[] => {
    const value = resolveField(object, field);
    return Array.isArray(value) ? value : [];
};

export const setChangedFieldsHighlighted = (
    schema: TFormElement,
    values: { previous: unknown; new: unknown; path: string[]; hasChangedOrder?: boolean }[] // Invariant: `path` is distinct across elements
): TFormElement => {
    const propsFor = ({ path: fieldPath }: { path: string }) => {
        const modifiedFields = values
            .filter(({ previous: previousValue, new: newValue, hasChangedOrder }) => {
                const fieldsDifferent = !areObjectsEqual(
                    resolveField(previousValue, fieldPath),
                    resolveField(newValue, fieldPath)
                );
                return hasChangedOrder ? fieldsDifferent && hasChangedOrder : fieldsDifferent;
            })
            .map(({ path }) => joinFieldPath([...path, fieldPath]));
        return { highlighted: modifiedFields };
    };
    switch (schema.type) {
        case UiSchemaType.LAYOUT_VERTICAL:
        case UiSchemaType.LAYOUT_HORIZONTAL:
        case UiSchemaType.REGION:
            return {
                ...schema,
                elements: schema.elements.map((element) => setChangedFieldsHighlighted(element, values)),
            };
        case UiSchemaType.LAYOUT_GRID:
        case UiSchemaType.LAYOUT_RESPONSIVE:
            return {
                ...schema,
                elements: schema.elements.map(({ element, ...rest }) => ({
                    ...rest,
                    element: setChangedFieldsHighlighted(element, values),
                })),
            };
        case UiSchemaType.PANEL:
        case UiSchemaType.OPTIONAL_PANEL:
            return {
                ...schema,
                element: schema.element ? setChangedFieldsHighlighted(schema.element, values) : undefined,
            };
        case UiSchemaType.INPUT:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                    highlightedOnlyIfNonEmpty: !propsFor(schema),
                },
            };
        case UiSchemaType.NUMBER:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.AUTOCOMPLETE:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.AUTOCOMPLETE_ASYNC:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.CHECKBOX:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.DATE:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.DATE_WITH_FORCED_GENEVA_TIMEZONE:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.DATETIME:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.TIME:
            return {
                ...schema,
                typeProperties: {
                    ...schema.typeProperties,
                    ...propsFor(schema),
                },
            };
        case UiSchemaType.FIELD_ARRAY:
            const { path: field } = schema;
            const groupBy: (value: unknown) => string | number = (value) => {
                if (value && typeof value === 'object') {
                    const obj = value as Record<string, any>;
                    if ('id' in obj && obj.id != null) return obj.id;
                    for (const key of Object.keys(obj)) {
                        const nested = obj[key];
                        if (nested && typeof nested === 'object') {
                            if ('id' in nested && nested.id != null) {
                                return nested.id;
                            }
                        }
                    }
                }
                return JSON.stringify(value);
            };
            const createMap = (object: unknown): Partial<Record<string, { value: unknown; index: number }>> =>
                Object.fromEntries(
                    resolveFieldArray(object, field).map((child, i) => [groupBy(child), { value: child, index: i }])
                );
            const valueMaps = values.map(({ previous: previousValue, new: newValue, path }) => ({
                previous: createMap(previousValue),
                new: createMap(newValue),
                path,
            }));
            const childrenData = valueMaps.map(({ previous: previousValueMap, new: newValueMap, path }) => {
                // Removed elements cannot be highlighted because they don't exist anymore; but we can still display some information on the UI
                const removedKeys = Object.keys(previousValueMap).filter((key) => !(key in newValueMap));
                const hasRemovedElements = removedKeys.length > 0;
                // Additionally, two items may not have changed in their content, but they could have been re-ordered
                const newChildren = Object.keys(newValueMap).map((key) => {
                    const previousChild = previousValueMap[key],
                        newChild = newValueMap[key]!; // By definition
                    const hasChangedOrder = newChild.index !== previousChild?.index;
                    const childPath = [...path, field, String(newChild.index)];
                    return { path: childPath, previous: previousChild?.value, new: newChild.value, hasChangedOrder };
                });
                return {
                    newChildren,
                    hasRemovedElements,
                };
            });
            // Distinct invariant should be preserved
            const childrenFlat = childrenData.flatMap(({ newChildren }) => newChildren);

            return {
                ...schema,
                // TODO: include usage of `hasRemovedElements`
                element: setChangedFieldsHighlighted(schema.element, childrenFlat),
            };
        case UiSchemaType.FIELD_ARRAY_TABS:
            throw new Error(); // Unsupported
        case UiSchemaType.RANGE:
        case UiSchemaType.RICH_TEXT:
        case UiSchemaType.CUSTOM:
        case UiSchemaType.FILTER_GROUP:
            return schema;
    }
};
